/**
 * StreamMonitorStatus - Shows the status of automatic stream detection
 * Displays which broadcasts are being monitored and auto-start/auto-end settings
 */
import { useState, useEffect } from "react";
import { Radio, Activity, Clock, RefreshCw, Settings, Check, X } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const StreamMonitorStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [timeoutMinutes, setTimeoutMinutes] = useState(4);
  const [savingTimeout, setSavingTimeout] = useState(false);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/broadcasts/stream-monitor/status`, { headers: getAuthHeader() });
      setStatus(response.data);
      setTimeoutMinutes(response.data.inactivity_timeout_minutes || 4);
    } catch (error) {
      console.error("Error fetching stream monitor status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchStatus(); 
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const triggerCheck = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/broadcasts/stream-monitor/check`, {}, { headers: getAuthHeader() });
      await fetchStatus();
    } catch (error) {
      console.error("Error triggering check:", error);
    } finally {
      setChecking(false);
    }
  };

  const updateTimeout = async () => {
    setSavingTimeout(true);
    try {
      await axios.put(`${API}/broadcasts/stream-monitor/inactivity-timeout`, 
        { minutes: timeoutMinutes }, 
        { headers: getAuthHeader() }
      );
      await fetchStatus();
      setShowSettings(false);
    } catch (error) {
      console.error("Error updating timeout:", error);
    } finally {
      setSavingTimeout(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/30 border border-white/5 p-4 mb-4">
        <div className="flex items-center gap-2 text-zinc-500">
          <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          <span className="text-xs">Loading stream monitor status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-green-500/10 to-zinc-900/30 border border-green-500/20 p-4 mb-4" data-testid="stream-monitor-status">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
            <Activity className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              Stream Activity Monitor
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-[10px] font-normal text-green-400 uppercase tracking-wider">Active</span>
            </h4>
            <p className="text-[11px] text-zinc-500">
              Broadcasts auto-start when streams go live, auto-end live status after {status?.inactivity_timeout_minutes || 4} min of inactivity (games stay scheduled until manually completed)
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={triggerCheck}
            disabled={checking}
            className="bg-zinc-800 text-white hover:bg-zinc-700 text-[10px] px-3 py-1.5 h-auto rounded-none"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking...' : 'Check Now'}
          </Button>
          <Button
            onClick={() => setShowSettings(!showSettings)}
            className="bg-zinc-800 text-white hover:bg-zinc-700 text-[10px] px-2 py-1.5 h-auto rounded-none"
          >
            <Settings className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider block mb-1">
                Inactivity Timeout (minutes)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={3}
                  max={10}
                  value={timeoutMinutes}
                  onChange={(e) => setTimeoutMinutes(Math.min(10, Math.max(3, parseInt(e.target.value) || 4)))}
                  className="w-16 bg-zinc-800 border border-white/10 text-white text-sm px-2 py-1"
                />
                <Button
                  onClick={updateTimeout}
                  disabled={savingTimeout}
                  className="bg-green-600 text-white hover:bg-green-500 text-[10px] px-2 py-1 h-auto rounded-none"
                >
                  {savingTimeout ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </Button>
              </div>
              <p className="text-[9px] text-zinc-600 mt-1">
                Broadcasts end automatically if no stream data for this long (3-10 min)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Monitored Broadcasts */}
      {status?.broadcasts && status.broadcasts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <p className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">
            Currently Monitoring ({status.broadcasts.length})
          </p>
          <div className="space-y-1">
            {status.broadcasts.map(bc => (
              <div 
                key={bc.broadcast_id} 
                className="flex items-center justify-between bg-zinc-800/50 px-2 py-1.5 text-xs"
              >
                <div className="flex items-center gap-2">
                  <Radio className="w-3 h-3 text-green-400" />
                  <span className="text-white font-mono">{bc.broadcast_id.slice(0, 8)}...</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] ${bc.inactive_minutes > 2 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                    Inactive: {bc.inactive_minutes}m
                  </span>
                  <span className={`text-[10px] ${bc.will_auto_end_in < 1 ? 'text-red-400' : 'text-zinc-500'}`}>
                    Auto-end in: {bc.will_auto_end_in}m
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!status?.broadcasts || status.broadcasts.length === 0) && (
        <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          No live broadcasts being monitored. Streams will auto-start when detected.
        </div>
      )}
    </div>
  );
};

export default StreamMonitorStatus;
